<script>
import Modal from "@/components/g-modal.vue";
import FormModal from "@/components/form-modal.vue";
import { mapGetters } from "vuex";
import { TabEnum, WorkflowReviewEnum } from "../businessplan.types";

import BusinessPlanCore from "./mixins/businessplan.core";
import BusinessPlanEvidance from "./mixins/businessplan.evidence";
import BusinessPlanFinancial from "./mixins/businessplan.financial";
import BusinessPlanRoadmap from "./mixins/businessplan.roadmap";
import BusinessPlanScroll from "./mixins/businessplan.scroll";
import BusinessPlanVersioning from "./mixins/businessplan.versioning";
import BusinessPlanWorkflow from "./mixins/businessplan.workflow";

import WorkflowDisplay from "./shared/workflow-display.vue";

import Alocacao from "./windows/alocacao.vue";
import Evidencias from "./windows/evidencias.vue";
import BeneficioEconomico from "./windows/beneficio-economico.vue";
import BPForm from "./windows/businessplan.vue";
import OutrasDespesas from "./windows/outras-despesas.vue";
import Roadmap from "./windows/roadmap.vue";
import VPL from "./windows/vpl.vue";

export default {
  components: {
    Alocacao,
    BeneficioEconomico,
    BPForm,
    Evidencias,
    FormModal,
    Modal,
    OutrasDespesas,
    Roadmap,
    VPL,
    WorkflowDisplay
  },
  mixins: [
    BusinessPlanCore,
    BusinessPlanEvidance,
    BusinessPlanFinancial,
    BusinessPlanRoadmap,
    BusinessPlanScroll,
    BusinessPlanVersioning,
    BusinessPlanWorkflow
  ],
  data() {
    return {
      opts: {
        statusRubrica: [
          {
            id: 1,
            value: "Ativo"
          },
          {
            id: 0,
            value: "Inativo"
          }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["fromPage"])
  },
  created() {
    if (this.businessPlanId) {
      if (!this.clientId) {
        this.goBack();
        return;
      }

      this.doLoad();
      this.doLoadRoadmap({ skip: true });
      this.getPlatforms();
      this.initializeLocalWorkflow();
    }

    if (this.$route.query?.tab === "alocacao") {
      this.currentStep = TabEnum.ALOCACAO;
    } else if (this.$route.query?.tab === "vpl") {
      this.currentStep = TabEnum.VPL;
    }
  },
  methods: {
    goBack() {
      if (this.fromPage === "ativacaoped-workflow-acompanhamento") {
        this.$router.back();
      } else {
        this.$router.push({
          name: "ativacaoped-projetos"
        });
      }
    },
    async sendWorkflowFoward() {
      try {
        const nextId = this.currentWorkflow?.nextStep?.id;
        const responsible = this.formData?.responsaveis ?? [];
        const selectedNextReviwers = responsible.filter(
          ({ workflow_id }) => workflow_id === nextId
        );
        const reviwersIds = selectedNextReviwers.map(
          ({ responsavel_id }) => responsavel_id
        );

        await this.sendToNextWorkflow(WorkflowReviewEnum.APPROVE, reviwersIds);

        this.doLoad();
      } catch {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro ao enviar o Business Plan para a próxima etapa",
          text: "Tente novamente e caso o erro persista, contacte o suporte."
        });
      }
    }
  },
  watch: {
    clientId(next, old) {
      if (next !== old) {
        this.$router.push({
          name: "ativacaoped-projetos"
        });
      }
    }
  }
};
</script>

<template>
  <v-card class="form-container">
    <v-item-group
      v-model="currentStep"
      class="tab-container"
      :class="{ 'shadow-header': shadowHeader }"
      mandatory
    >
      <v-item
        v-for="{ disabled, errors, id, label, tooltip } in tabs"
        v-slot="{ active }"
        :value="id"
        :key="id"
      >
        <v-tooltip :disabled="!tooltip" top>
          <template v-slot:activator="{ attrs, on }">
            <div v-bind="attrs" v-on="on">
              <v-btn
                class="tab"
                :class="{ active }"
                dense
                :disabled="disabled"
                text
                tile
                @click="currentStep = id"
              >
                <v-badge :content="errors" color="red" inline :value="errors">
                  {{ label }}
                </v-badge>
              </v-btn>
            </div>
          </template>

          <span>{{ tooltip }}</span>
        </v-tooltip>
      </v-item>
    </v-item-group>

    <v-window ref="window" v-model="currentStep">
      <v-window-item eager :value="TabEnum.BP">
        <BPForm
          ref="bpform"
          :data.sync="formData"
          :editable="editable"
          :hasAdminRole="hasAdminRole"
          @error="onError(TabEnum.BP, $event)"
        />
      </v-window-item>
      <v-window-item eager :value="TabEnum.BENEFICIO_ECONOMICO">
        <BeneficioEconomico
          ref="beneficioEconomico"
          :data.sync="formData"
          :editable="editable"
          :hasAdminRole="hasAdminRole"
          @error="onError(TabEnum.BENEFICIO_ECONOMICO, $event)"
        />
      </v-window-item>
      <v-window-item :eager="!!businessPlanId" :value="TabEnum.ROADMAP">
        <Roadmap
          ref="roadmap"
          :canEditStatus="isDemandant || hasAdminRole || isSystemAdmin"
          :editable="editable"
          :hasAdminRole="hasAdminRole"
          :hasDiscovery="hasDiscovery"
          :isOficial="selectedVersion.versao_oficial"
          :platforms="platforms"
          :rows="roadmapRows"
          @delete="doDeleteRoadmap"
          @error="onError(TabEnum.ROADMAP, $event)"
          @load="doLoadRoadmap"
          @save="doSaveRoadmap(TabEnum.ROADMAP, $event)"
          @save-status="doSaveRoadmapStatus"
        />
      </v-window-item>
      <v-window-item :eager="!!businessPlanId" :value="TabEnum.ALOCACAO">
        <Alocacao
          ref="alocacao"
          :editable="editable"
          :endDate="formData.data_fim"
          :hasAdminRole="hasAdminRole"
          :inWorkflowAdjustment="inWorkflowReview"
          :nonPeople="!!formData.non_people"
          :officialVersion="selectedVersion.versao_oficial"
          :participants.sync="formData.participantes"
          :startDate="formData.data_inicio"
          :status="formData.status"
          @error="onError(TabEnum.ALOCACAO, $event)"
        />
      </v-window-item>
      <v-window-item :value="TabEnum.OUTRAS_DESPESAS">
        <OutrasDespesas
          ref="outrasDespesas"
          :editable="editable"
          :endDate="formData.data_fim"
          :hasAdminRole="hasAdminRole"
          :nonPeople="!!formData.non_people"
          :officialVersion="selectedVersion.versao_oficial"
          :startDate="formData.data_inicio"
          :status="formData.status"
          @error="onError(TabEnum.OUTRAS_DESPESAS, $event)"
          @reload="reloadVPL()"
        />
      </v-window-item>
      <v-window-item :eager="!!businessPlanId" :value="TabEnum.VPL">
        <VPL
          ref="vpl"
          :disabled="tabsDisabled[TabEnum.VPL]"
          :editable="formData.editavel"
          :endDate="formData.data_fim"
          :isDemandant="isDemandant"
          :hasAdminRole="hasAdminRole"
          :officialVersion="selectedVersion.versao_oficial"
          :startDate="formData.data_inicio"
          :status="formData.status"
          :usefulLife="formData.vida_util"
          @error="onError(TabEnum.VPL, $event)"
          @reload="reloadRubricas()"
        />
      </v-window-item>
      <v-window-item
        :eager="!!businessPlanId"
        :value="TabEnum.MEMORIA_EVIDENCIAS"
      >
        <Evidencias
          ref="memoriaEvidencias"
          :editable="editable"
          :hasAdminRole="hasAdminRole"
          :value="formData.anexos || []"
          @error="onError(TabEnum.MEMORIA_EVIDENCIAS, $event)"
        />
      </v-window-item>
    </v-window>

    <Modal ref="approveErrorModal" title="Não enviado para aprovação">
      <p>
        Não foi possivel enviar o Business Plan para aprovação pois um ou mais
        itens obrigatórios não foram definidos.
      </p>
      <p>
        Por favor, preencha todas as abas sinalizadas antes de continuar com a
        aprovação.
      </p>
    </Modal>

    <Modal ref="approveVersionModal" title="Aprovar versão">
      <p>
        Deseja realmente aprovar esta versão? Ela se tornará a versão oficial do Business Plan.
      </p>
      <template #buttons="{ close }">
        <v-spacer />
        <v-btn class="px-5 ml-3" color="secondary" depressed @click="close(false)">Cancelar</v-btn>
        <v-btn class="px-5 ml-3" color="primary" dark depressed @click="close(true); approveVersion()">Aprovar</v-btn>
      </template>
    </Modal>

    <Modal ref="reproveVersionModal" title="Reprovar versão">
      <p>
        Deseja realmente reprovar esta versão? Esta ação não pode ser desfeita.
      </p>
      <template #buttons="{ close }">
        <v-spacer />
        <v-btn class="px-5 ml-3" color="secondary" depressed @click="close(false)">Cancelar</v-btn>
        <v-btn class="px-5 ml-3" color="error" dark depressed @click="close(true); reproveVersion()">Reprovar</v-btn>
      </template>
    </Modal>

    <Modal ref="turnMeRevisorConfirmModal" title="Revisar">
      <p>
        Você está preste a se tornar um revisor deste Business Plan.
      </p>
      <p>
        Deseja continuar?
      </p>

      <template #buttons="{ close }">
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          depressed
          @click="close(false)"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="primary"
          dark
          depressed
          @click="close(true)"
        >
          Revisar
        </v-btn>
      </template>
    </Modal>

    <Modal ref="confirmRoadmapDelete" title="Confirmar exclusão">
      <p>
        Você está preste a apagar um entregável do Business Plan. Essa ação não
        pode ser desfeita.
      </p>
      <p>
        Deseja continuar?
      </p>

      <template #buttons="{ close }">
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          depressed
          @click="close(false)"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="error"
          dark
          depressed
          @click="close(true)"
        >
          Excluir
        </v-btn>
      </template>
    </Modal>

    <FormModal
      :cols="workflowReviewFields"
      :opened.sync="workflowRevisorsModal.opened"
      :opts="opts"
      title="Definição de responsável"
      titleIcon="mdi-note-text-outline"
      :value.sync="workflowRevisorsModal.data"
      @save="saveNextWorkflowReviewers"
    />

    <FormModal
      :cols="workflowFields"
      :opened.sync="workflowModal.opened"
      :opts="opts"
      :title="workflowModal.title"
      titleIcon="mdi-note-text-outline"
      :value.sync="workflowModal.data"
      @save="workflowSendReview"
    />

    <FormModal
      :cols="reviewFields"
      :opened.sync="reviewModal.opened"
      :opts="opts"
      title="Adicionar comentário"
      titleIcon="mdi-note-text-outline"
      :value.sync="reviewModal.data"
      @save="sendRequestedChangesToApproval"
    />

    <transition-group
      name="fade"
      tag="div"
      class="buttons-container"
      :class="{ 'shadow-footer': shadowFooter }"
    >
      <v-select
        v-if="showVersioning"
        class="select-version-field"
        dense
        :disabled="disableVersionSelect"
        hide-details
        key="versionSelect"
        :items="formData.versoes"
        :item-text="item => `v${item.versao} - ${item.label}`"
        item-value="id"
        outlined
        :value="formData.id"
        @change="goToVersion"
      ></v-select>
      <WorkflowDisplay
        v-else-if="formData.workflow"
        class="ml-5 mr-auto"
        :comments="formData.comentarios"
        :current="formData.workflow"
        :responsible="formData.responsaveis"
        key="workflowDisplay"
      />

      <v-spacer key="spacer"></v-spacer>

      <v-btn color="secondary" depressed key="cancelBtn" @click="goBack()">
        {{ editable ? "Cancelar" : "Voltar para listagem" }}
      </v-btn>

      <v-btn
        v-show="showSaveBtn"
        color="primary"
        depressed
        key="saveBtn"
        @click="doSave()"
      >
        Salvar
      </v-btn>

      <v-btn
        v-if="isDemandant && inFilling"
        color="primary"
        depressed
        key="sendToApprovalBtn"
        @click="sendToApproval()"
      >
        Enviar para aprovação
      </v-btn>

      <v-btn
        v-if="isDemandant && inVersionFilling"
        color="primary"
        depressed
        key="sendToApprovalBtn"
        @click="sendVersionToRevision()"
      >
        Enviar para aprovação
      </v-btn>

      <v-btn
        v-if="isDemandant && inWorkflowReview"
        color="primary"
        depressed
        key="sendToApprovalBtn"
        @click="requestedChangesValidation()"
      >
        Enviar ajustes
      </v-btn>

      <template v-if="showVersioningActions">
        <v-btn
          v-if="selectedVersion.versao_oficial && selectedVersion.isLast"
          color="primary"
          depressed
          key="createVersionBtn"
          @click="createVersion()"
        >
          Criar nova versão
        </v-btn>

        <template
          v-else-if="!selectedVersion.versao_oficial && selectedVersion.isLast"
        >
          <v-btn
            v-if="!isDemandant"
            color="error"
            depressed
            key="reproveVersionBtn"
            @click="$refs.reproveVersionModal.asyncOpen()"
          >
            Reprovar esta versão
          </v-btn>

          <v-btn
            v-if="!isDemandant"
            color="primary"
            depressed
            key="approveVersionBtn"
            @click="$refs.approveVersionModal.asyncOpen()"
          >
            Aprovar esta versão
          </v-btn>
        </template>
      </template>
      <template v-else-if="formData.workflow">
        <template v-if="showWorkflowActionBtns">
          <v-btn
            color="secondary"
            depressed
            key="requestChangeBtn"
            @click="workflowOpenModal('change')"
          >
            Solicitar ajuste(s)
          </v-btn>

          <v-btn
            color="primary"
            depressed
            key="approveWorkflowBtn"
            @click="workflowOpenModal('approve')"
          >
            Aprovar
          </v-btn>

          <v-btn
            color="error"
            depressed
            key="reproveWorkflowBtn"
            @click="workflowOpenModal('reprove')"
          >
            Reprovar
          </v-btn>
        </template>

        <v-btn
          v-if="showTurnMeReviewerBtn"
          color="primary"
          depressed
          key="turnMeApproverBtn"
          @click="turnMeReviewer()"
        >
          Revisar
        </v-btn>

        <v-btn
          v-if="showSelectReviewBtn"
          color="primary"
          depressed
          key="openSelectRevisorsModalBtn"
          @click="openSelectReviewersModal()"
        >
          Escolher revisor(es)
        </v-btn>

        <v-btn
          v-if="showChangeWorkflowBtn"
          color="primary"
          depressed
          key="openSelectRevisorsModalBtn"
          @click="sendWorkflowFoward()"
        >
          Enviar para próxima etapa
        </v-btn>
      </template>
    </transition-group>
  </v-card>
</template>

<style lang="scss" scoped>
.form-container {
  height: calc(100% - 1rem);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tab-container + .v-window.v-item-group {
  padding: 1.25rem 0.75rem 0 0.75rem;
  overflow-y: auto;
  flex-grow: 1;
  width: 100%;

  ::v-deep {
    .v-window__container {
      &,
      & .v-window-item {
        height: 100%;
      }
    }
  }
}

.buttons-container {
  background-color: #fff;
  display: flex;
  padding: 1rem 1.25rem 1rem 0;
  position: relative;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
  z-index: 1;

  &.shadow-footer {
    box-shadow: 0 -2px 4px -1px rgba(0, 0, 0, 0.1),
      0 -4px 5px 0 rgba(0, 0, 0, 0.07), 0 -1px 10px 0 rgba(0, 0, 0, 0.06);
  }

  .v-btn {
    margin-left: 0.5rem;
  }

  .select-version-field {
    margin: 0 auto 0 1rem;
    max-width: max-content;
    height: 36px;

    &::v-deep {
      .v-input__slot {
        min-height: unset;

        fieldset {
          height: 42px;
        }

        .v-select__slot {
          height: 36px;

          .v-input__append-inner {
            margin-top: 6px;
          }
        }
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  width: 0 !important;
  min-width: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

.fade-enter-to,
.fade-leave {
  opacity: 1;
  width: auto;
}

.tab-container {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  padding: 0.75rem 0.75rem 0 0.75rem;
  text-align: center;
  transition: box-shadow 0.3s ease;
  z-index: 1;

  &.shadow-header {
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1),
      0 4px 5px 0 rgba(0, 0, 0, 0.07), 0 1px 10px 0 rgba(0, 0, 0, 0.06);
  }

  .tab {
    display: flex;
    opacity: 0.8;
    padding: 0 1rem;

    &.active {
      opacity: 1;
      box-shadow: 0 5px 0 var(--v-table-header-base);
    }
  }
}
</style>
